import gitpodLogoLarge from "@/assets/gitpod-logo-large.svg";
import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { Button } from "@/components/flexkit/Button";
import { DesktopSignin } from "@/components/login/DesktopSignin";
import { LoginFooter } from "@/components/login/LoginFooter";
import type { PropsWithClassName } from "@/components/podkit/lib/cn";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { useBootInAppChat } from "@/hooks/use-boot-in-app-chat";
import { useDesktop } from "@/hooks/use-desktop";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useLocalRunner } from "@/hooks/use-local-runner";
import { useGetAccount, useListLoginProviders } from "@/queries/account-queries";
import { ContinueWithSSOForm } from "@/routes/login/ContinueWithSSOForm";
import { DefaultSSOForm } from "@/routes/login/DefaultSSOForm";
import { OnaOnboardingLayout } from "@/routes/login/OnaOnboardingLayout";
import { OnaWaitlistLayout } from "@/routes/login/OnaWaitlistLayout";
import { useFlag } from "@/hooks/use-flag";
import { Flags } from "@/feature-flags";
import { LocalRunnerStatus } from "frontend-shared/local-runner";
import { useCallback, useEffect, useState, type FC } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";

type LoginPageProps = {
    onaLayout?: boolean;
};

export const LoginPage: FC<LoginPageProps> = ({ onaLayout = false }) => {
    const { value: globalOverloadPercentage } = useFlag(Flags.GlobalOverload, 0);
    const shouldShowWaitlist = globalOverloadPercentage > 0;

    useDocumentTitle("Login");
    useBootInAppChat(1000);

    const [searchParams] = useSearchParams();

    const inviteId = searchParams.get("inviteId") || undefined;

    const location = useLocation();
    const fromPath = (location.state?.from?.pathname || "") as string;

    const navigateTo = searchParams.get("navigateTo") || fromPath || "/";

    // The returnTo URL for the login flow.
    const returnTo = searchParams.get("returnTo") || new URL(navigateTo, window.location.origin).toString();

    const { data: account, isPending: isLoadingAccount } = useGetAccount();
    const { data: loginProvidersData, isPending: isPendingProviders } = useListLoginProviders(inviteId);

    const localRunner = useLocalRunner();
    const { isDesktop } = useDesktop();

    // Calculate all variables before any conditional returns
    const needsRunnerScreen = LocalRunnerStatus.needsRunnerScreen(localRunner.status);
    const errorDismissed = localRunner.isRunnerErrorDismissed;

    // Check if we should auto-redirect for custom domain: single provider + allowCustom=false
    const shouldAutoRedirect =
        loginProvidersData &&
        !loginProvidersData.allowCustom &&
        loginProvidersData.providers.length === 1 &&
        loginProvidersData.providers[0].loginUrl;

    // Auto-redirect immediately - must be before any early returns
    useEffect(() => {
        if (shouldAutoRedirect) {
            window.location.href = loginProvidersData.providers[0].loginUrl;
        }
    }, [shouldAutoRedirect, loginProvidersData]);

    // Wait for the user to be loaded
    if (!account && isLoadingAccount) {
        return <LoadingState />;
    }
    // This is only available in the desktop app.
    // Checking this separately to avoid confusion with the account loading state.
    if (localRunner.loading) {
        return <LoadingState />;
    }

    if (needsRunnerScreen && !errorDismissed) {
        return <Navigate to="/local-runner" />;
    }

    // If user is already logged in, redirect
    if (account && !isLoadingAccount) {
        // Using the relative navigateTo path from <Navigate> in AuthenticatedUserLayout.
        return <Navigate to={navigateTo} replace />;
    }

    // Show loading state while fetching login providers or during auto-redirect
    if (isPendingProviders || shouldAutoRedirect) {
        return <LoadingState />;
    }

    if (onaLayout) {
        return shouldShowWaitlist ? (
            <OnaWaitlistLayout userEmail={account?.email} />
        ) : (
            <OnaOnboardingLayout inviteId={inviteId} returnTo={returnTo} />
        );
    }

    return (
        <div
            data-testid="login-page"
            className="flex h-full flex-col items-center justify-between gap-4 overflow-visible p-6 sm:overflow-y-auto"
        >
            <div className="my-auto flex max-w-[368px] flex-col items-center gap-7">
                <img src={gitpodLogoLarge} alt="Gitpod logo" width="64" height="64" />
                <div className="flex flex-col items-center gap-2">
                    <Heading1 className="text-center text-2xl font-medium tracking-tight text-content-primary">
                        Ona
                    </Heading1>
                    <Text className="max-w-xs text-center text-xl text-content-secondary">
                        Automated, standardized, and secure development environments
                    </Text>
                </div>
                {isDesktop && <DesktopSignin />}
                {!isDesktop && <SSOLogin inviteId={inviteId} returnTo={returnTo} />}
            </div>

            <LoginFooter />
        </div>
    );
};

type SSOLoginProps = {
    returnTo: string;
    inviteId?: string;
    hideLoginToClassic?: boolean;
    newLoginLayout?: boolean;
    onSSOViewChange?: (showContinueWithSSO: boolean) => void;
} & PropsWithClassName;

export const SSOLogin: FC<SSOLoginProps> = ({
    returnTo,
    inviteId,
    hideLoginToClassic,
    newLoginLayout,
    className,
    onSSOViewChange,
}) => {
    const { data: loginProvidersData, isPending: isPendingProviders } = useListLoginProviders(inviteId);

    const [searchParams] = useSearchParams();
    const errorParm = searchParams.get("error");
    const errorDescriptionParm = searchParams.get("error_description");
    const customDomainParam = searchParams.get("custom_domain");

    if (errorParm) {
        console.log("error param on login page", errorParm, errorDescriptionParm);
    }

    const [showContinueWithSSO, setCustomSSO] = useState(false);

    const handleSSOViewChange = useCallback(
        (show: boolean) => {
            setCustomSSO(show);
            onSSOViewChange?.(show);
        },
        [onSSOViewChange],
    );

    if (isPendingProviders) {
        return <LoadingState />;
    }

    const isCustomDomainError = errorParm === "custom_domain_required";

    return (
        <div id="sso-login-container" className={className}>
            {showContinueWithSSO ? (
                <ContinueWithSSOForm
                    className="flex w-full flex-col items-stretch gap-2"
                    returnTo={returnTo}
                    onBack={() => handleSSOViewChange(false)}
                />
            ) : (
                <DefaultSSOForm
                    className="flex w-full flex-col items-stretch gap-2"
                    returnTo={returnTo}
                    loginProviders={loginProvidersData?.providers}
                    allowCustom={loginProvidersData?.allowCustom}
                    onContinueWithSSO={() => handleSSOViewChange(true)}
                    hideLoginToClassic={hideLoginToClassic}
                    newLoginLayout={newLoginLayout}
                />
            )}
            {errorParm && (
                <div className="mt-6 flex flex-row gap-3 rounded-xl border border-border-base bg-surface-glass px-3 py-4">
                    <IconInfo size="base" className="text-content-orange" />
                    <div className="flex w-full grow flex-col gap-2">
                        {isCustomDomainError && customDomainParam ? (
                            <>
                                <Text className="text-base font-semibold">Custom Domain Required</Text>
                                <Text className="text-sm">
                                    Your organization requires access through a custom domain ({customDomainParam}).
                                </Text>
                                <Button
                                    size="lg"
                                    variant="primary"
                                    className="mt-2 w-full"
                                    asChild
                                    data-tracking-id="go-to-custom-domain"
                                >
                                    <a href={`https://${customDomainParam}`}>Go to {customDomainParam}</a>
                                </Button>
                                <Text className="mt-2 text-xs text-content-tertiary">
                                    If you&apos;re an organization administrator and need help, please contact your IT
                                    team.
                                </Text>
                            </>
                        ) : (
                            <>
                                <Text className="text-base">
                                    Oops! Looks like your sign-in was interrupted. Please try again!
                                </Text>
                                {errorDescriptionParm && (
                                    <Text className="text-sm">Provider says: {errorDescriptionParm}</Text>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
